/**
 * The cookie question, on every page of the site.
 *
 * It is mounted once at the root of the React application and once as an
 * island in the Astro layout, so both halves of superschema.ai ask the same
 * question with the same words and write the same answer to the same key.
 *
 * It shows when this browser has not answered and has not sent Global
 * Privacy Control. It never shows again once answered, and the "Cookie
 * preferences" link in either footer brings it back.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { DsIsland } from '@/design-system/island'
import { Button } from '@/design-system/primitives'
import {
  CONSENT_OPEN_EVENT,
  setConsent,
  shouldAskForConsent,
} from '@/utils/consent'
import '@/design-system/consent.css'

/** Exported so a test can read the words without rendering the whole shell. */
export const COOKIE_CONSENT_COPY = {
  lead: 'Nothing here is measuring you yet.',
  rest: 'Say yes and we can use Google Analytics and a session recorder to see which pages help and where the product breaks. The cookies that sign you in run either way.',
  more: 'Change your answer any time from the Cookie preferences link in the footer.',
  policy: 'Read the cookie policy',
  accept: 'Accept',
  decline: 'Decline',
}

export default function CookieConsent() {
  /*
    Closed on the first render and opened in an effect, rather than read
    straight into state. localStorage is not available while a page is being
    rendered on the server or prerendered by Astro, and a bar that renders in
    the HTML and then disappears is a bar that flashes at every visitor who
    already answered.
  */
  const [asking, setAsking] = useState(false)
  const barRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (shouldAskForConsent()) setAsking(true)
    const reopen = () => setAsking(true)
    window.addEventListener(CONSENT_OPEN_EVENT, reopen)

    /*
      The footers are two different things: a React component that can call
      openCookiePreferences directly, and a static Astro footer rendered into
      HTML with no script of its own. One delegated listener serves both, so
      the control is `data-cookie-preferences` on a button and nothing else
      has to know this component exists.
    */
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target?.closest?.('[data-cookie-preferences]')) return
      event.preventDefault()
      setAsking(true)
    }
    document.addEventListener('click', onClick)

    return () => {
      window.removeEventListener(CONSENT_OPEN_EVENT, reopen)
      document.removeEventListener('click', onClick)
    }
  }, [])

  /*
    The floating human-and-robot switcher is fixed to the bottom right at
    z-index 50, and on a phone this bar is full width, so the switcher landed
    on top of the Accept button. The bar publishes its own height and the
    switcher steps above it for as long as the question is open, in
    shell.css, where that component is declared.
  */
  useLayoutEffect(() => {
    const root = document.documentElement
    if (!asking || !barRef.current) {
      root.removeAttribute('data-consent-open')
      root.style.removeProperty('--ds-consent-h')
      return
    }
    root.setAttribute('data-consent-open', '')
    root.style.setProperty('--ds-consent-h', `${barRef.current.offsetHeight}px`)
    return () => {
      root.removeAttribute('data-consent-open')
      root.style.removeProperty('--ds-consent-h')
    }
  }, [asking])

  if (!asking) return null

  const answer = (choice: 'granted' | 'denied') => {
    setConsent(choice)
    setAsking(false)
  }

  /*
    The bare island, the same scope RedesignBanner uses: a `.ds` root paints
    paper, and this one wraps a fixed child and is otherwise zero height, so
    it takes the scope and the theme without the ground.
  */
  return (
    <DsIsland panel={false} ground={false}>
      {/*
        A region, not a dialog. Nothing is trapped and nothing is blocked, so
        a person can read the cookie policy in the same page before deciding.
      */}
      <section
        ref={barRef}
        className="ds-consent"
        role="region"
        aria-label="Cookie choices"
        data-testid="cookie-consent"
      >
        <div className="ds-consent__say">
          <b>{COOKIE_CONSENT_COPY.lead}</b> {COOKIE_CONSENT_COPY.rest}
          <div className="ds-consent__more">
            {COOKIE_CONSENT_COPY.more}{' '}
            {/* A plain anchor: this component renders inside the Astro pages
                too, where there is no router to give a Link a context. */}
            <a className="ds-link" href="/cookies">
              {COOKIE_CONSENT_COPY.policy}
            </a>
          </div>
        </div>
        <div className="ds-consent__acts">
          <Button variant="secondary" onClick={() => answer('denied')}>
            {COOKIE_CONSENT_COPY.decline}
          </Button>
          <Button variant="secondary" onClick={() => answer('granted')}>
            {COOKIE_CONSENT_COPY.accept}
          </Button>
        </div>
      </section>
    </DsIsland>
  )
}
